import api from "./api";
import { resolveRoleName } from "../utils/auth";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;
const FULL_LIST_LIMIT = 1000;

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const extractMeta = (payload, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
  if (payload?.meta && typeof payload.meta === "object") {
    return {
      total: Number(payload.meta.total) || 0,
      page: Number(payload.meta.page) || Number(page) || DEFAULT_PAGE,
      limit: Number(payload.meta.limit) || Number(limit) || DEFAULT_LIMIT,
      totalPages: Number(payload.meta.totalPages) || 1,
    };
  }
  const data = extractArray(payload);
  return {
    total: data.length,
    page: Number(page) || DEFAULT_PAGE,
    limit: Number(limit) || DEFAULT_LIMIT,
    totalPages: 1,
  };
};

const hydrateParentFields = (rows) => {
  const normalized = (rows || []).map(normalizeFunction);
  const functionMap = new Map(normalized.map((f) => [f.function_id, f]));
  normalized.forEach((f) => {
    if (f.parent_id && functionMap.has(f.parent_id)) {
      const parent = functionMap.get(f.parent_id);
      f.parent_name = parent.name;
      f.parent_code = parent.function_code;
    }
  });
  return normalized;
};

const toQuery = ({ page, limit, search } = {}) => {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim())
    query.append("search", String(search).trim());
  return query.toString();
};

const normalizeFunction = (dto) => ({
  function_id: dto?.function_id ?? dto?.id,
  function_code: dto?.function_code ?? dto?.functionCode ?? dto?.code ?? "",
  name: dto?.name ?? "",
  company_id:
    dto?.company_id ?? dto?.companyId ?? dto?.company?.company_id ?? null,
  company_name: dto?.company_name ?? dto?.company?.name ?? "",
  parent_id:
    dto?.parent_id ??
    dto?.parent_function_id ??
    dto?.Function?.function_id ??
    dto?.parentFunction?.function_id ??
    null,
  parent_name:
    dto?.parent_name ?? dto?.Function?.name ?? dto?.parentFunction?.name ?? "",
  parent_code:
    dto?.parent_code ??
    dto?.Function?.function_code ??
    dto?.Function?.functionCode ??
    dto?.parentFunction?.function_code ??
    dto?.parentFunction?.functionCode ??
    "",
  description: dto?.description ?? dto?.overview ?? "",
  background_color: dto?.background_color ?? dto?.backgroundColor ?? null,
  created_by: dto?.created_by ?? dto?.createdBy ?? null,
  updated_by: dto?.updated_by ?? dto?.updatedBy ?? null,
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
  // passthroughs
  company: dto?.company ?? null,
  Function: dto?.Function ?? null,
  parentFunction: dto?.parentFunction ?? null,
  creator: dto?.creator ?? null,
  updater: dto?.updater ?? null,
  jobs:
    Array.isArray(dto?.jobs) ? dto.jobs
    : Array.isArray(dto?.job) ? dto.job
    : [],
});



export async function getFunctionsPaginated({
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  search = "",
} = {}) {
  const qs = toQuery({ page, limit, search });
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/function" : (
      "/hierarchical-view/relation/function/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);
  const normalized = hydrateParentFields(extractArray(res));
  const meta = extractMeta(res, page, limit);
  return { data: normalized, meta };
}

// New: generic list for Function Management table
export async function getFunctions() {
  try {
    if (getFunctions._cache) return getFunctions._cache;
    if (getFunctions._inflight) return await getFunctions._inflight;
    // Backend now returns paginated payload; fetch all accessible rows for
    // backward-compatible consumers that expect a flat array.
    getFunctions._inflight = (async () => {
      const first = await getFunctionsPaginated({
        page: 1,
        limit: FULL_LIST_LIMIT,
        search: "",
      });
      let all = Array.isArray(first?.data) ? first.data.slice() : [];
      const totalPages = Number(first?.meta?.totalPages) || 1;
      if (totalPages > 1) {
        for (let p = 2; p <= totalPages; p++) {
          const next = await getFunctionsPaginated({
            page: p,
            limit: FULL_LIST_LIMIT,
            search: "",
          });
          if (Array.isArray(next?.data) && next.data.length > 0) {
            all = all.concat(next.data);
          }
        }
      }
      const byId = new Map();
      all.forEach((f) => {
        if (f?.function_id == null) return;
        byId.set(Number(f.function_id), f);
      });
      const deduped = Array.from(byId.values());
      getFunctions._cache = deduped;
      return deduped;
    })();
    const out = await getFunctions._inflight;
    return out;
  } catch {
    return [];
  } finally {
    getFunctions._inflight = null;
  }
}

// Get functions filtered by company
export async function getFunctionsByCompany(companyId) {
  try {
    const cid = Number(companyId);
    if (!Number.isInteger(cid) || cid <= 0) return [];

    const res = await api.get(`/function/company/${cid}`);
    const rows = extractArray(res);
    return hydrateParentFields(rows);
  } catch {
    return [];
  }
}

export async function getFunctionById(functionId) {
  try {
    const res = await api.get(`/function/${functionId}`);
    return res ? normalizeFunction(res) : null;
  } catch (e) {
    return null;
  }
}

export async function patchFunction(functionId, payload) {
  try {
    const body = {};
    if (
      payload.functionCode !== undefined ||
      payload.function_code !== undefined
    )
      body.functionCode = String(
        payload.functionCode ?? payload.function_code,
      ).trim();
    if (payload.name !== undefined) body.name = String(payload.name).trim();
    if (payload.company_id !== undefined)
      body.company_id = Number(payload.company_id);
    if (
      payload.parent_function_id !== undefined ||
      payload.parent_id !== undefined
    ) {
      const pid = payload.parent_function_id ?? payload.parent_id;
      body.parent_function_id = pid != null && pid !== "" ? Number(pid) : null;
    }
    if (payload.overview !== undefined || payload.description !== undefined)
      body.overview = String(payload.overview ?? payload.description);
    if (
      payload.backgroundColor !== undefined ||
      payload.background_color !== undefined
    )
      body.backgroundColor =
        payload.backgroundColor ?? payload.background_color ?? null;

    const res = await api.patch(`/function/${functionId}`, body);
    const normalized = res ? normalizeFunction(res) : null;
    // Invalidate cached list so subsequent gets are fresh
    if (typeof getFunctions === "function") {
      getFunctions._cache = null;
    }
    return normalized;
  } catch (e) {
    throw e;
  }
}

export async function createFunction(payload) {
  try {
    const body = {
      functionCode: String(
        payload.functionCode ?? payload.function_code ?? "",
      ).trim(),
      name: String(payload.name ?? "").trim(),
      company_id: Number(payload.company_id),
      parent_function_id: (() => {
        const pid = payload.parent_function_id ?? payload.parent_id;
        return pid != null && pid !== "" ? Number(pid) : null;
      })(),
      overview: String(payload.overview ?? payload.description ?? ""),
      backgroundColor:
        payload.backgroundColor ?? payload.background_color ?? null,
    };
    const res = await api.post("/function", body);
    const normalized = res ? normalizeFunction(res) : null;
    // Invalidate cached list so subsequent gets are fresh
    if (typeof getFunctions === "function") {
      getFunctions._cache = null;
    }
    return normalized;
  } catch (e) {
    throw e;
  }
}

export async function deleteFunction(functionId) {
  try {
    await api.delete(`/function/${functionId}`);
    // Invalidate cached list so subsequent gets are fresh
    if (typeof getFunctions === "function") {
      getFunctions._cache = null;
    }
    return true;
  } catch (e) {
    throw e;
  }
}

// Legacy bulk endpoint support (used by ADMIN/NORMAL_USER flows in some pages)
// Falls back to RBAC /function if bulk endpoint is unavailable.
export async function getFunctionsByUsersBulk(userIds = []) {
  const ids =
    Array.isArray(userIds) ?
      userIds.map((n) => Number(n)).filter(Number.isInteger)
    : [];

  if (ids.length === 0) return getFunctions();

  const key = ids
    .slice()
    .sort((a, b) => a - b)
    .join(",");

  if (!getFunctionsByUsersBulk._inflight)
    getFunctionsByUsersBulk._inflight = new Map();
  if (getFunctionsByUsersBulk._inflight.has(key)) {
    return await getFunctionsByUsersBulk._inflight.get(key);
  }

  const p = (async () => {
    try {
      const res = await api.post("/hierarchical-view/relation/function/bulk", {
        userIds: ids,
      });

      const results =
        Array.isArray(res?.results) ? res.results
        : Array.isArray(res) ? res
        : [];

      if (results.length > 0) {
        const collected = [];
        for (const item of results) {
          const data = item?.data || {};
          const assoc =
            Array.isArray(data.associatedFunctions) ?
              data.associatedFunctions
            : [];
          const created =
            Array.isArray(data.createdFunctions) ? data.createdFunctions : [];
          collected.push(...assoc, ...created);
        }
        const byId = new Map();
        collected.forEach((f) => {
          const normalized = normalizeFunction(f);
          const id = Number(normalized?.function_id);
          if (Number.isInteger(id) && id > 0 && !byId.has(id)) {
            byId.set(id, normalized);
          }
        });
        return hydrateParentFields(Array.from(byId.values()));
      }

      // Some environments may return a plain list/paginated shape instead of results[]
      return hydrateParentFields(extractArray(res));
    } catch {
      return getFunctions();
    }
  })();

  getFunctionsByUsersBulk._inflight.set(key, p);
  try {
    return await p;
  } finally {
    getFunctionsByUsersBulk._inflight.delete(key);
  }
}
